import React, { useMemo } from 'react';
import { Dimensions, ScrollView, TouchableOpacity, View } from 'react-native';

import PieChart from '@/components/PieChart';
import { ThemedText } from '@/components/ThemedText';
import { getPartyColor } from '@/constants/Colors';
import AppStyles from '@/constants/Styles';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useCampaignStore } from '@/stores/campaignStore';
import { StreetBreakdown, Voter } from '@/types/canvassing';
import { PollingStationInfo } from '@/types/polling_station';

interface InfoProps {
  engagementInfo: any;
  isUserInEngagement: boolean;
  onJoinEngagement: () => void;
  onLeaveEngagement: () => void;
  joining: boolean;
  leaving: boolean;
  insets: any;
  engagementId?: string;
  streetBreakdown?: StreetBreakdown[] | null;
  isTellingEngagement?: boolean;
  voters?: any[];
  pollingStationInfo?: PollingStationInfo | null;
  battlePlanType?: string | undefined;
}

export default function Info({ 
  engagementInfo, 
  isUserInEngagement, 
  onJoinEngagement, 
  onLeaveEngagement, 
  joining, 
  leaving, 
  insets,
  engagementId,
  streetBreakdown,
  isTellingEngagement = false,
  voters = [],
  pollingStationInfo,
  battlePlanType
}: InfoProps) {
  // Theme-aware colors
  const cardBackground = useThemeColor({}, 'cardBackground' as any);
  const borderColor = useThemeColor({}, 'border' as any);
  const progressBackground = useThemeColor({}, 'progressBackground' as any);
  const progressVisitedColor = useThemeColor({}, 'progressVisited' as any);
  const progressNotInColor = useThemeColor({}, 'progressNotIn' as any);
  const progressHighColor = useThemeColor({}, 'progressHigh' as any);
  const progressMediumColor = useThemeColor({}, 'progressMedium' as any);
  const progressLowColor = useThemeColor({}, 'progressLow' as any);
  const secondaryTextColor = useThemeColor({}, 'secondaryText' as any);
  
  // Get addresses data for VI analysis
  const getAddressesForEngagement = useCampaignStore(state => state.getAddressesForEngagement);
  const addresses = engagementId ? getAddressesForEngagement(engagementId) : [];


  // VI label mapping
  const viLabels: { [key: string]: string } = {
    'R': 'Reform',
    'Z': 'Possible Reform',
    'A': 'Against Reform',
    'C': 'Conservative',
    'G': 'Green',
    'I': 'Independent',
    'L': 'Liberal Democrat',
    'N': 'Nationalist',
    'S': 'Labour',
    'U': 'Undecided',
    'W': 'Will not vote',
  };

  const isGotvKnocking = battlePlanType === 'gotv-door-knocking' || battlePlanType === 'gotv-phoning';

  const pledges = engagementInfo?.rz_total - engagementInfo?.rz_will_not_vote || 0;
  const votersVoted = engagementInfo?.rz_voted || 0;
  const votersContacted = (engagementInfo?.rz_promised_to_vote || 0) + (engagementInfo?.rz_voters_not_in || 0);
  const votersNotYetContacted = pledges - votersContacted - votersVoted;

  const calculateCombinedProgress = (): number => {
    if (!engagementInfo) return 0;
    
    if (isGotvKnocking) {
      return pledges > 0 ? ((votersVoted + votersContacted) / pledges) * 100 : 0;
    } else {
      // Standard canvassing logic
      return engagementInfo.address_count > 0 ? ((engagementInfo.addresses_visited + engagementInfo.addresses_not_in) / engagementInfo.address_count) * 100 : 0;
    }
  };

  const calculateVisitedProgress = (): number => {
    if (!engagementInfo) return 0;
    
    if (isGotvKnocking) {
      return pledges > 0 ? ((votersVoted) / pledges) * 100 : 0;
    } else {
      // Standard canvassing logic
      return engagementInfo.address_count > 0 ? (engagementInfo.addresses_visited / engagementInfo.address_count) * 100 : 0;
    }
  };

  const calculateNotInProgress = (): number => {
    if (!engagementInfo) return 0;
    
    if (isGotvKnocking) {
      return pledges > 0 ? ((votersContacted) / pledges) * 100 : 0;
    } else {
      // Standard canvassing logic
      return engagementInfo.address_count > 0 ? (engagementInfo.addresses_not_in / engagementInfo.address_count) * 100 : 0;
    }
  };

  // Voter statistics for telling engagements
  const calculateVotedCount = (): number => {
    if (!isTellingEngagement || !voters) return 0;
    return voters.filter(voter => voter.voted_yet || voter.voted).length;
  };

  const calculateVotingProgress = (): number => {
    if (!isTellingEngagement || !voters || voters.length === 0) return 0;
    const votedCount = calculateVotedCount();
    return (votedCount / voters.length) * 100;
  };

  // VI Analysis functions
  const analyzeVIResults = useMemo(() => {
    if (!addresses.length) {
      return { todayData: [], allTimeData: [] };
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Midnight today
    
    // Get all voters from visited addresses only
    const visitedAddresses = addresses.filter(addr => addr.visited);
    const allVoters = visitedAddresses.flatMap(addr => addr.voters || []);
    
    // Separate voters by interaction date
    const todayVoters: Voter[] = [];
    const allTimeVoters: Voter[] = [];
    
    allVoters.forEach(voter => {
      if (voter.latest_vi && voter.latest_vi_datetime) {
        try {
          const interactionDate = new Date(voter.latest_vi_datetime);
          if (interactionDate >= today) {
            todayVoters.push(voter);
          }
          allTimeVoters.push(voter);
        } catch {
          // If date parsing fails, include in all time
          allTimeVoters.push(voter);
        }
      }
    });

    // Count VI codes (excluding Undecided and Will not vote)
    const countVI = (voters: Voter[]): { [key: string]: number } => {
      const counts: { [key: string]: number } = {};
      voters.forEach(voter => {
        if (voter.latest_vi && voter.latest_vi !== null && voter.latest_vi !== 'U' && voter.latest_vi !== 'W') {
          counts[voter.latest_vi] = (counts[voter.latest_vi] || 0) + 1;
        }
      });
      return counts;
    };

    // Count Undecided and Will not vote separately
    const countUndecidedAndWillNotVote = (voters: Voter[]): { undecidedCount: number; willNotVoteCount: number } => {
      let undecidedCount = 0;
      let willNotVoteCount = 0;
      voters.forEach(voter => {
        if (voter.latest_vi === 'U') {
          undecidedCount++;
        } else if (voter.latest_vi === 'W') {
          willNotVoteCount++;
        }
      });
      return { undecidedCount, willNotVoteCount };
    };

    const todayCounts = countVI(todayVoters);
    const allTimeCounts = countVI(allTimeVoters);
    const todayUndecidedAndWillNotVote = countUndecidedAndWillNotVote(todayVoters);
    const allTimeUndecidedAndWillNotVote = countUndecidedAndWillNotVote(allTimeVoters);

    // Check if today's data only contains U (Undecided) and W (Will not vote)
    const todayOnlyUndecidedOrWillNotVote = todayVoters.length > 0 && 
      todayVoters.every(voter => voter.latest_vi === 'U' || voter.latest_vi === 'W');

    // Convert to chart data
    const createChartData = (counts: { [key: string]: number }): any[] => {
      const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
      return Object.entries(counts)
        .filter(([code]) => code !== null && code !== undefined)
        .map(([code, count]) => ({
          label: viLabels[code] || code,
          shortLabel: code, // Use the VI code as short label
          value: count,
          color: getPartyColor(code) || '#808080',
          percentage: total > 0 ? (count / total) * 100 : 0
        }))
        .sort((a, b) => b.value - a.value); // Sort by count descending
    };

    return {
      todayData: todayOnlyUndecidedOrWillNotVote ? [] : createChartData(todayCounts),
      allTimeData: createChartData(allTimeCounts),
      todayVoterCount: todayOnlyUndecidedOrWillNotVote ? 0 : todayVoters.length,
      allTimeVoterCount: allTimeVoters.length,
      todayUndecidedAndWillNotVote,
      allTimeUndecidedAndWillNotVote
    };
  }, [addresses, viLabels]);

  const combinedProgress = isTellingEngagement ? calculateVotingProgress() : calculateCombinedProgress();
  const visitedProgress = calculateVisitedProgress();
  const notInProgress = calculateNotInProgress();
  const votedCount = calculateVotedCount();
  const totalVoters = voters?.length || 0;

  // Reusable chart component
  const renderChartSection = (
    title: string, 
    data: any[], 
    voterCount: number, 
    undecidedAndWillNotVote: { undecidedCount: number; willNotVoteCount: number },
    showMarginBottom: boolean = false
  ) => (
    <View style={showMarginBottom ? { marginBottom: 20 } : {}}>
      <ThemedText style={{ fontSize: 14, fontWeight: '600', textAlign: 'center' }}>
        {title} ({voterCount} voters)
      </ThemedText>
      <PieChart data={data} size={200} />
      {/* Undecided and Will not vote counts */}
      {(undecidedAndWillNotVote.undecidedCount > 0 || undecidedAndWillNotVote.willNotVoteCount > 0) && (
        <View style={{ alignItems: 'center' }}>
          <ThemedText style={{ fontSize: 12, color: secondaryTextColor }}>
            {undecidedAndWillNotVote.undecidedCount > 0 && `Undecided: ${undecidedAndWillNotVote.undecidedCount}`}
            {undecidedAndWillNotVote.undecidedCount > 0 && undecidedAndWillNotVote.willNotVoteCount > 0 && ' • '}
            {undecidedAndWillNotVote.willNotVoteCount > 0 && `Will not vote: ${undecidedAndWillNotVote.willNotVoteCount}`}
          </ThemedText>
        </View>
      )}
    </View>
  );

  return (
    <ScrollView style={{ flex: 1, padding: 16 }} contentContainerStyle={{ paddingBottom: insets.bottom }}>
      {engagementInfo ? (
        <>
          {/* Progress Section */}
          <ThemedText style={AppStyles.engagementName}>
            {engagementInfo?.number_within_battle_plan || engagementInfo?.station_name || 'Engagement'}{engagementInfo?.engagement_name ? `: ${engagementInfo.engagement_name}` : ''}
          </ThemedText>
          <View style={{ marginVertical: 12 }}>
            {/* Progress Header with Percentage Badge */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <ThemedText style={AppStyles.engagementStats}>
                {isTellingEngagement ? 'Voting Progress' : 
                 isGotvKnocking ? 'GOTV Progress' : 'Address Progress'}
              </ThemedText>
              <View style={{ 
                backgroundColor: (() => {
                  if (isGotvKnocking) {
                    if (votersContacted + votersVoted === 0) return '#808080'; // Grey for not started
                    if (votersNotYetContacted < 5) return progressHighColor; // Green
                    if (votersNotYetContacted <= 50) return progressMediumColor; // Yellow
                    return progressLowColor; // Red
                  } else {
                    return combinedProgress >= 80 ? progressHighColor : combinedProgress >= 50 ? progressMediumColor : progressLowColor;
                  }
                })(), 
                paddingHorizontal: 12, 
                paddingVertical: 6, 
                borderRadius: 16,
                minWidth: 60,
                alignItems: 'center'
              }}>
                <ThemedText style={{ color: '#ffffff', fontSize: 14, fontWeight: 'bold' }}>
                  {(() => {
                    if (isGotvKnocking) {
                      return `${votersNotYetContacted} left`;
                    } else {
                      return `${Math.round(combinedProgress)}%`;
                    }
                  })()}
                </ThemedText>
              </View>
            </View>
            
            {/* Progress Bar */}
            {isTellingEngagement ? (
              /* Simple progress bar for telling */
              <View style={{ 
                height: 8, 
                backgroundColor: progressBackground, 
                borderRadius: 4, 
                marginBottom: 16,
                overflow: 'hidden',
                flexDirection: 'row'
              }}>
                {combinedProgress > 0 && (
                  <View style={{ 
                    height: '100%', 
                    backgroundColor: progressVisitedColor, 
                    width: `${combinedProgress}%`,
                    borderRadius: 4
                  }} />
                )}
              </View>
            ) : (
              /* Stacked Progress Bar for canvassing */
              <View style={{ 
                height: 8, 
                backgroundColor: progressBackground, 
                borderRadius: 4, 
                marginBottom: 16,
                overflow: 'hidden',
                flexDirection: 'row'
              }}>
                {/* Visited segment */}
                {visitedProgress > 0 && (
                  <View style={{ 
                    height: '100%', 
                    backgroundColor: progressVisitedColor, 
                    width: `${visitedProgress}%`,
                    borderRadius: 4
                  }} />
                )}
                {/* Not In segment */}
                {notInProgress > 0 && (
                  <View style={{ 
                    height: '100%', 
                    backgroundColor: progressNotInColor, 
                    width: `${notInProgress}%`,
                    borderRadius: 4
                  }} />
                )}
              </View>
            )}
            
            {/* Stats Card */}
            <View style={{ 
              backgroundColor: cardBackground, 
              padding: 16, 
              borderRadius: 8, 
              marginBottom: 16 
            }}>
              {isTellingEngagement ? (
                /* Voter Stats for Telling */
                <>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                    <ThemedText style={AppStyles.engagementStats}>Voted</ThemedText>
                    <ThemedText style={AppStyles.engagementStats}>{votedCount}</ThemedText>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                    <ThemedText style={AppStyles.engagementStats}>Total Voters</ThemedText>
                    <ThemedText style={AppStyles.engagementStats}>{totalVoters}</ThemedText>
                  </View>
                </>
              ) : isGotvKnocking ? (
                /* GOTV Stats for Door Knocking */
                <>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                    <ThemedText style={AppStyles.engagementStats}>Voted</ThemedText>
                    <ThemedText style={AppStyles.engagementStats}>{engagementInfo?.rz_voted || 0}</ThemedText>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                    <ThemedText style={AppStyles.engagementStats}>Voters Not In</ThemedText>
                    <ThemedText style={AppStyles.engagementStats}>{(engagementInfo?.rz_voters_not_in || 0)}</ThemedText>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                    <ThemedText style={AppStyles.engagementStats}>Voters Voting Later</ThemedText>
                    <ThemedText style={AppStyles.engagementStats}>{(engagementInfo?.rz_promised_to_vote || 0)}</ThemedText>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                    <ThemedText style={AppStyles.engagementStats}>Voters Not Voting</ThemedText>
                    <ThemedText style={AppStyles.engagementStats}>{(engagementInfo?.rz_will_not_vote || 0)}</ThemedText>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                    <ThemedText style={AppStyles.engagementStats}>R/Z Pledges</ThemedText>
                    <ThemedText style={AppStyles.engagementStats}>{(engagementInfo?.rz_total || 0)}</ThemedText>
                  </View>
                </>
              ) : (
                /* Address Stats for Regular Canvassing */
                <>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                    <ThemedText style={AppStyles.engagementStats}>Addresses Visited</ThemedText>
                    <ThemedText style={AppStyles.engagementStats}>{(engagementInfo?.addresses_visited || 0) + (engagementInfo?.addresses_not_in || 0)} of {engagementInfo?.address_count || 0}</ThemedText>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                    <ThemedText style={AppStyles.engagementStats}>Addresses Not In</ThemedText>
                    <ThemedText style={AppStyles.engagementStats}>{engagementInfo?.addresses_not_in || 0}</ThemedText>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                    <ThemedText style={AppStyles.engagementStats}>Remaining Addresses</ThemedText>
                    <ThemedText style={AppStyles.engagementStats}>
                      {(engagementInfo?.address_count || 0) - (engagementInfo?.addresses_visited || 0) - (engagementInfo?.addresses_not_in || 0)}
                    </ThemedText>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                    <ThemedText style={AppStyles.engagementStats}>Total Voters</ThemedText>
                    <ThemedText style={AppStyles.engagementStats}>{engagementInfo?.voter_count || 0}</ThemedText>
                  </View>
                </>
              )}
            </View>
          </View>

          {/* Polling Station Info Section */}
          {pollingStationInfo && (
            <View style={{ 
              backgroundColor: cardBackground, 
              padding: 16, 
              borderRadius: 8, 
              marginBottom: 16 
            }}>
              <ThemedText style={AppStyles.engagementName}>Polling Station</ThemedText>
              <View style={{ marginTop: 8 }}>
                <ThemedText style={[AppStyles.engagementStats]}>
                  {pollingStationInfo.polling_station.address}
                </ThemedText>
                <ThemedText style={AppStyles.engagementStats}>{pollingStationInfo.polling_station.postcode}</ThemedText>
              </View>
            </View>
          )}

          {/* VI Breakdown Chart Section - Only show for canvassing */}
          {!isTellingEngagement && analyzeVIResults.allTimeData.length > 0 && (
            <>
              <View style={{ 
                backgroundColor: cardBackground, 
                padding: 16, 
                borderRadius: 8, 
                marginBottom: 16 
              }}>

                {/* Chart Display */}
                {(analyzeVIResults.todayVoterCount || 0) > 0 && (
                  renderChartSection(
                    "Today's Results",
                    analyzeVIResults.todayData,
                    analyzeVIResults.todayVoterCount || 0,
                    analyzeVIResults.todayUndecidedAndWillNotVote || { undecidedCount: 0, willNotVoteCount: 0 },
                    (analyzeVIResults.allTimeVoterCount || 0) > (analyzeVIResults.todayVoterCount || 0)
                  )
                )}
                {(analyzeVIResults.allTimeVoterCount || 0) > 0 && (
                  renderChartSection(
                    "All Time Results",
                    analyzeVIResults.allTimeData,
                    analyzeVIResults.allTimeVoterCount || 0,
                    analyzeVIResults.allTimeUndecidedAndWillNotVote || { undecidedCount: 0, willNotVoteCount: 0 }
                  )
                )}
              </View>
            </>
          )}

          {/* Campaigners Section */}
          <ThemedText style={AppStyles.engagementName}>Campaigners</ThemedText>
          <View style={AppStyles.campaignerList}>
            {engagementInfo?.active_campaigner_names?.length > 0 ? (
              [...engagementInfo.active_campaigner_names]
                .filter((name): name is string => name !== null && name !== undefined)
                .sort((a, b) => a.localeCompare(b))
                .map((name: string, index: number) => (
                  <ThemedText key={index} style={AppStyles.campaignerItem}>{name}</ThemedText>
                ))
            ) : (
              <ThemedText style={AppStyles.campaignerItem}>No active campaigners</ThemedText>
            )}
          </View>

          <>
              {/* Join/Leave Buttons */}
              {isUserInEngagement ? (
                <TouchableOpacity
                  style={[
                    AppStyles.secondaryButton,
                    { 
                      marginTop: 20,
                      marginBottom: 16,
                    }
                  ]}
                  onPress={onLeaveEngagement}
                  disabled={leaving}
                >
                  <ThemedText style={AppStyles.secondaryButtonText}>
                    {leaving ? 'Leaving...' : isTellingEngagement ? 'Leave Polling Station' : 'Leave Walk Route'}
                  </ThemedText>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={[
                    AppStyles.primaryButton,
                    { 
                      marginTop: 20,
                      marginBottom: 16,
                    }
                  ]}
                  onPress={onJoinEngagement}
                  disabled={joining}
                >
                  <ThemedText style={AppStyles.primaryButtonText}>
                    {joining ? 'Joining...' : isTellingEngagement ? 'Join Polling Station' : 'Join Walk Route'}
                  </ThemedText>
                </TouchableOpacity>
              )}
          </>

          {/* Street Breakdown Section */}
          {streetBreakdown && streetBreakdown.length > 0 && (
            <>

              <ThemedText style={AppStyles.engagementName}>Streets</ThemedText>
              <View style={AppStyles.campaignerList}>
                {[...streetBreakdown]
                  .sort((a, b) => {
                    // Sort by street name first, then by street sub name
                    if (a.street_name !== b.street_name) {
                      return a.street_name.localeCompare(b.street_name);
                    }
                    // If street names are the same, sort by street sub name
                    const aSubName = a.street_sub_name || '';
                    const bSubName = b.street_sub_name || '';
                    return aSubName.localeCompare(bSubName);
                  })
                  .map((street, index, sortedStreets) => {
                    const screenWidth = Dimensions.get('window').width;
                    const isNarrowScreen = screenWidth < 400;
                    
                    return (
                      <View key={index} style={{ 
                        paddingVertical: 8,
                        borderBottomWidth: index < sortedStreets.length - 1 ? 1 : 0,
                        borderBottomColor: borderColor
                      }}>
                        <View style={{ 
                          flexDirection: isNarrowScreen ? 'column' : 'row', 
                          justifyContent: isNarrowScreen ? 'flex-start' : 'space-between',
                          alignItems: isNarrowScreen ? 'flex-start' : 'center'
                        }}>
                          <ThemedText style={{ 
                            fontSize: 16,
                            marginBottom: isNarrowScreen ? 4 : 0,
                            flex: isNarrowScreen ? 0 : 1
                          }}>
                            {street.street_sub_name ? `${street.street_sub_name}, ${street.street_name}` : street.street_name}
                            {street.postcodes.length > 0 && (
                              <ThemedText> ({street.postcodes.join(', ')})</ThemedText>
                            )}
                          </ThemedText>
                          <ThemedText style={{ 
                            fontSize: 14, 
                            color: secondaryTextColor,
                            alignSelf: isNarrowScreen ? 'flex-start' : 'flex-end'
                          }}>
                            {street.voter_count} voter{street.voter_count !== 1 ? 's' : ''} / {street.address_count} address{street.address_count !== 1 ? 'es' : ''}
                          </ThemedText>
                        </View>
                      </View>
                    );
                  })}
              </View>
            </>
          )}

        </>
      ) : (
        <View style={AppStyles.emptyContainer}>
          <ThemedText>Loading walk route info...</ThemedText>
        </View>
      )}
    </ScrollView>
  );
}
